// Session-scoped snapshots; preserve subscribers across invalidation.
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { PurchaseOrderService } from '../purchase-order.service';

export interface PoQuery {
  searchParam: string;
  userId: number;
  docStatus: string;
  fromDate: string | null;
  toDate: string | null;
  sortField: string;
  sortDirection: string;
  limit: number;
  offset: number;
  activeFlag: 0 | 1;
  operationType?: string;
}
interface CacheEntry {
  subject: BehaviorSubject<any>;
  updatedAt: number;
  version: number;
  inFlight?: Promise<void>;
}
@Injectable({ providedIn: 'root' })
export class BuyerPoSWRService {
  private cache = new Map<string, CacheEntry>();
  private readonly TTL_MS = 30_000;
  constructor(private poService: PurchaseOrderService, private cookies: CookieService) {}
  private clone(value: any): any { return value == null ? value : JSON.parse(JSON.stringify(value)); }
  private entryFor(key: string): CacheEntry {
    let entry = this.cache.get(key);
    if (!entry) {
      // Retain live subscriptions; discard an inactive snapshot first.
      if (this.cache.size >= 40) {
        for (const [oldKey, oldEntry] of this.cache) {
          if (!oldEntry.subject.observers.length) { this.cache.delete(oldKey); break; }
        }
      }
      entry = {subject:new BehaviorSubject(null), updatedAt:0, version:0};
      this.cache.set(key, entry);
    }
    return entry;
  }
  getList$(q: PoQuery, revalidate = true): Observable<any> {
    const entry = this.entryFor(this.keyOf(q));
    this.getCached(q);
    if (revalidate) void this.refresh(q).catch(() => {});
    return entry.subject.pipe(map(value => this.clone(value)));
  }
  getCached(q: PoQuery): any {
    const entry = this.cache.get(this.keyOf(q));
    if (!entry) return null;
    if (Date.now() - entry.updatedAt >= this.TTL_MS) {
      if (entry.subject.value !== null) entry.subject.next(null);
      return null;
    }
    return this.clone(entry.subject.value);
  }
  refresh(q: PoQuery): Promise<void> {
    const key = this.keyOf(q);
    const entry = this.entryFor(key);
    if (entry.inFlight) return entry.inFlight;
    const version = entry.version;
    const pending = Promise.resolve().then(() => this.poService.fetchAllPurchaseOrderByBuyerId(
      q.searchParam, q.userId, q.docStatus, q.fromDate, q.toDate, q.sortField,
      q.sortDirection, q.limit, q.offset, q.activeFlag, q.operationType ?? 'buyerPurchaseOrder'
    )).then(response => {
      if (version !== entry.version || this.cache.get(key) !== entry || key !== this.keyOf(q)) throw {status:409};
      if (!Array.isArray(response?.rows) || response.totalElements == null) throw {status:502};
      entry.updatedAt = Date.now();
      entry.subject.next(this.clone(response));
    }).finally(() => {
      if (entry.inFlight === pending) entry.inFlight = undefined;
    });
    entry.inFlight = pending;
    return pending;
  }
  invalidateMatching(predicate: (key: string) => boolean): void {
    for (const [key, entry] of this.cache) {
      if (!predicate(key)) continue;
      entry.version++;
      entry.inFlight = undefined;
      entry.updatedAt = 0;
      entry.subject.next(null);
      if (!entry.subject.observers.length) this.cache.delete(key);
    }
  }
  invalidateAll(): void { this.invalidateMatching(() => true); }
  isMutation(url: string, method: string): boolean {
    if (/^(GET|HEAD|OPTIONS)$/i.test(method)) return false;
    const path = url.split('?')[0];
    return /\/(purchaseOrder|buyer|supplier|users|rfq)\//i.test(path)
      && !/^(get|fetch|search|filter|check|verify|export)/i.test(path.split('/').pop() || '');
  }
  keyOf(q: PoQuery): string {
    const identity = ['userId','userRole','roleId','companyId','clientId','tokenId','tokenHash'].map(key => this.cookies.get(key));
    return JSON.stringify(['buyer-po', ...identity, q.userId, q.searchParam, q.docStatus,
      q.fromDate, q.toDate, q.sortField, q.sortDirection, q.limit, q.offset,
      q.activeFlag, q.operationType ?? 'buyerPurchaseOrder']);
  }
  patchList(q: PoQuery, mutator: (response: any) => any): void {
    const entry = this.cache.get(this.keyOf(q));
    if (!entry?.subject.value) return;
    entry.subject.next(this.clone(mutator(this.clone(entry.subject.value))));
  }
}
